import { MESSAGE_ATTACHMENTS_TAG } from './attachments';

/** Tag wrapping the per-session Crowdex guidance appended to agent prompts. */
export const SESSION_GUIDANCE_TAG = 'session-guidance';
/** Tag wrapping the transcript replayed into a recreated ACP session. */
export const SESSION_BOOTSTRAP_TAG = 'session-bootstrap';
export const SESSION_BOOTSTRAP_PREAMBLE =
  'This ACP session was recreated. Rehydrate your context from the canonical Crowdex transcript below, then answer only the newest user message. Do not repeat work already completed.';

/** Opens the transcript a native agent's fresh session is given. */
export const TRANSCRIPT_PREAMBLE =
  'Continue this existing Crowdex conversation. Treat the following transcript as prior context:';
/** Opens the conversation's instructions in a prompt (agents without a system prompt of their own). */
export const INSTRUCTIONS_PREAMBLE = 'Crowdex conversation instructions:';
/** Opens the reminder of the settings the user changed since the agent's last turn. */
export const SETTINGS_CHANGED_PREAMBLE =
  'Since your last turn, the user changed the conversation settings. Follow the new ones; what the previous ones allowed or restricted no longer applies.';
/** Opens the prompt a member of a group conversation is told at its turn (`@crowdex/groups`). */
export const GROUP_TURN_PREAMBLE = 'Group conversation with the user';

const SCAFFOLDING_TAGS = [
  ...[SESSION_GUIDANCE_TAG, SESSION_BOOTSTRAP_TAG, MESSAGE_ATTACHMENTS_TAG].map(
    (tag) => `<${tag}>`,
  ),
  // Reminders (`<system_reminder id="…">`, an agent's `<system-reminder>`) and skills.
  '<system_reminder',
  '<system-reminder',
  '<skills_instructions>',
  '<skill>',
];
// Agents derive titles by truncating the prompt, sometimes after stripping tags.
const MIN_PREAMBLE_MATCH = 20;
const PREAMBLE_HEADS = [
  SESSION_BOOTSTRAP_PREAMBLE,
  TRANSCRIPT_PREAMBLE,
  INSTRUCTIONS_PREAMBLE,
  SETTINGS_CHANGED_PREAMBLE,
  GROUP_TURN_PREAMBLE,
].map((preamble) => preamble.slice(0, 40).toLowerCase());

/**
 * Whether text (typically an agent-reported title) comes from Crowdex prompt plumbing
 * rather than from the user, so it must never be shown as their words.
 */
export function isPromptScaffoldingText(value: string): boolean {
  const lower = value.toLowerCase();
  if (SCAFFOLDING_TAGS.some((tag) => lower.includes(tag))) return true;
  const text = lower
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(?:title|titre)\s*:\s*/, '')
    // Quotes, and the bracket a group turn's prompt opens with.
    .replace(/^["'“”‘’`[]+|["'“”‘’`]+$/g, '')
    .replace(/[\s…]+$/u, '')
    .replace(/\.{3}$/, '')
    .trim();
  return (
    text.length >= MIN_PREAMBLE_MATCH &&
    PREAMBLE_HEADS.some((head) => text.startsWith(head) || head.startsWith(text))
  );
}
